import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Mail, MapPin, Phone, Send, CheckCircle, Loader2,
  MessageCircle, Clock
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus('error');
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      content: 'support@wsafeai.com',
      subtext: 'We reply within 24 hours'
    },
    {
      icon: MessageCircle,
      title: 'Live Chat',
      content: 'Chat with AI Assistant',
      subtext: 'Available 24/7'
    },
    {
      icon: Clock,
      title: 'Office Hours',
      content: 'Mon - Fri: 9AM - 6PM EST',
      subtext: 'For live support'
    }
  ];

  return (
    <div className="page contact-page">
      <Header onEnrollClick={() => window.location.href = '/pricing'} />
      
      <main className="page-content">
        {/* Hero */}
        <section className="page-hero contact-hero">
          <div className="container">
            <span className="section-tag">Get in Touch</span>
            <h1 className="page-title">Contact Us</h1>
            <p className="page-subtitle">
              Have questions? We're here to help you on your AI journey
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="contact-section">
          <div className="container">
            <div className="contact-grid">
              {/* Contact Info */}
              <div className="contact-info">
                <h2>Let's Connect</h2>
                <p>
                  Whether you have questions about the course, need technical support, 
                  or want to explore enterprise options, we're here to help.
                </p>

                <div className="contact-cards">
                  {contactInfo.map((info, idx) => (
                    <div key={idx} className="contact-info-card">
                      <info.icon className="contact-icon" />
                      <div>
                        <h4>{info.title}</h4>
                        <p className="contact-content">{info.content}</p>
                        <span className="contact-subtext">{info.subtext}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Form */}
              <div className="contact-form-wrapper">
                {status === 'success' ? (
                  <div className="success-state">
                    <CheckCircle className="success-icon" />
                    <h3>Message Sent!</h3>
                    <p>Thank you for reaching out. We'll get back to you within 24 hours.</p>
                    <Button 
                      className="btn-secondary"
                      onClick={() => setStatus('idle')}
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form className="contact-form" onSubmit={handleSubmit}>
                    <h3>Send us a Message</h3>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">Name</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your name"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="subject">Subject</label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select a topic</option>
                        <option value="general">General Inquiry</option>
                        <option value="course">Course Questions</option>
                        <option value="technical">Technical Support</option>
                        <option value="billing">Billing & Payments</option>
                        <option value="enterprise">Enterprise Solutions</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="How can we help you?"
                        rows={5}
                        required
                      />
                    </div>

                    {status === 'error' && (
                      <div className="error-message">
                        Failed to send message. Please try again.
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      className="btn-primary submit-btn"
                      disabled={status === 'loading'}
                    >
                      {status === 'loading' ? (
                        <><Loader2 className="spinner" /> Sending...</>
                      ) : (
                        <><Send size={18} /> Send Message</>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
